import React, { useCallback, useEffect, useRef, useState } from "react";
import Pagination from "./Pagination";
import SupportTicketDetailModal from "./SupportTicketDetailModal";
import SupportTicketSelect from "./SupportTicketSelect";
import {
    fetchPolarisSupportTicket,
    fetchPolarisSupportTicketStats,
    fetchPolarisSupportTickets,
    formatSupportTicketDate,
    formatSupportTicketSeverity,
    formatSupportTicketStatus,
    SupportTicketDetail,
    SupportTicketSeverity,
    SupportTicketStats,
    SupportTicketStatus,
    SupportTicketSummary,
} from "../services/supportTicketsService";
import "../styles/polaris-admin.css";
import "../styles/support-tickets.css";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];
const FILTER_DEBOUNCE_MS = 300;

const STATUS_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "RAISED", label: "Raised" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "CLOSED", label: "Closed" },
];

const SEVERITY_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "HIGH", label: "High" },
    { value: "MEDIUM", label: "Medium" },
    { value: "LOW", label: "Low" },
];

type Filters = {
    ticketNumber: string;
    title: string;
    companyName: string;
    raisedByFirstName: string;
    raisedByLastName: string;
    severity: SupportTicketSeverity | "";
    status: SupportTicketStatus | "";
};

const emptyFilters: Filters = {
    ticketNumber: "",
    title: "",
    companyName: "",
    raisedByFirstName: "",
    raisedByLastName: "",
    severity: "",
    status: "",
};

const emptyStats: SupportTicketStats = { total: 0, raised: 0, inProgress: 0, closed: 0 };

export default function PolarisSupportPage() {
    const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
    const [stats, setStats] = useState<SupportTicketStats>(emptyStats);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<Filters>(emptyFilters);
    const [debouncedFilters, setDebouncedFilters] = useState<Filters>(emptyFilters);
    const [detail, setDetail] = useState<SupportTicketDetail | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const hasLoadedOnceRef = useRef(false);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            setDebouncedFilters(filters);
            setPage(0);
        }, FILTER_DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [filters]);

    const load = useCallback(async (pageNum: number, activeFilters: Filters, size: number) => {
        if (hasLoadedOnceRef.current) {
            setRefreshing(true);
        } else {
            setInitialLoading(true);
        }
        setError(null);
        const apiFilters = {
            ...activeFilters,
            status: activeFilters.status || undefined,
            severity: activeFilters.severity || undefined,
        };
        try {
            const [list, nextStats] = await Promise.all([
                fetchPolarisSupportTickets({ ...apiFilters, page: pageNum, size }),
                fetchPolarisSupportTicketStats(apiFilters),
            ]);
            setTickets(list.content);
            setTotalPages(list.totalPages);
            setTotalElements(list.totalElements);
            setPage(list.number);
            setStats(nextStats);
            hasLoadedOnceRef.current = true;
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to load support tickets.");
        } finally {
            setInitialLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        void load(page, debouncedFilters, pageSize);
    }, [load, page, debouncedFilters, pageSize]);

    const openDetail = async (ticket: SupportTicketSummary) => {
        try {
            const full = await fetchPolarisSupportTicket(ticket.id);
            setDetail(full);
            setDetailOpen(true);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to load ticket details.");
        }
    };

    const handleUpdated = (updated: SupportTicketDetail) => {
        setTickets((prev) =>
            prev.map((t) =>
                t.id === updated.id
                    ? {
                          ...t,
                          title: updated.title,
                          status: updated.status,
                          severity: updated.severity,
                          canEdit: updated.canEdit,
                          canClose: updated.canClose,
                      }
                    : t,
            ),
        );
        setDetail(updated);
        void load(page, debouncedFilters, pageSize);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setPage(0);
    };

    return (
        <section className="pa-section" data-testid="polaris-support-page">
            <h2 className="pa-section-title">Support</h2>

            <div className="pa-support-stats" data-testid="polaris-support-stats">
                <div className="pa-support-stat">
                    <span className="pa-support-stat-value" data-testid="polaris-support-stat-total">
                        {stats.total.toLocaleString()}
                    </span>
                    <span className="pa-support-stat-label">Total tickets</span>
                </div>
                <div className="pa-support-stat">
                    <span className="pa-support-stat-value" data-testid="polaris-support-stat-raised">
                        {stats.raised.toLocaleString()}
                    </span>
                    <span className="pa-support-stat-label">Raised</span>
                </div>
                <div className="pa-support-stat">
                    <span className="pa-support-stat-value" data-testid="polaris-support-stat-in-progress">
                        {stats.inProgress.toLocaleString()}
                    </span>
                    <span className="pa-support-stat-label">In Progress</span>
                </div>
                <div className="pa-support-stat">
                    <span className="pa-support-stat-value" data-testid="polaris-support-stat-closed">
                        {stats.closed.toLocaleString()}
                    </span>
                    <span className="pa-support-stat-label">Closed</span>
                </div>
            </div>

            {error && <div className="pa-error">{error}</div>}
            {initialLoading && <div className="pa-loading">Loading support tickets…</div>}

            {!initialLoading && !error && (
                <>
                    <div
                        className="pa-table-wrapper"
                        style={{ opacity: refreshing ? 0.55 : 1, transition: "opacity 0.15s ease" }}
                        aria-busy={refreshing}
                    >
                        <table className="pa-table" data-testid="polaris-support-table">
                            <thead>
                                <tr>
                                    <th>Ticket number</th>
                                    <th>Date</th>
                                    <th>Title</th>
                                    <th>Company / School</th>
                                    <th>Raised by</th>
                                    <th>Severity</th>
                                    <th>Status</th>
                                </tr>
                                <tr className="pa-filter-row">
                                    <th>
                                        <input
                                            className="pa-filter-input"
                                            placeholder="Filter…"
                                            value={filters.ticketNumber}
                                            data-testid="polaris-support-filter-number"
                                            onChange={(e) =>
                                                setFilters((f) => ({ ...f, ticketNumber: e.target.value }))
                                            }
                                        />
                                    </th>
                                    <th />
                                    <th>
                                        <input
                                            className="pa-filter-input"
                                            placeholder="Filter…"
                                            value={filters.title}
                                            data-testid="polaris-support-filter-title"
                                            onChange={(e) =>
                                                setFilters((f) => ({ ...f, title: e.target.value }))
                                            }
                                        />
                                    </th>
                                    <th>
                                        <input
                                            className="pa-filter-input"
                                            placeholder="Filter…"
                                            value={filters.companyName}
                                            data-testid="polaris-support-filter-company"
                                            onChange={(e) =>
                                                setFilters((f) => ({ ...f, companyName: e.target.value }))
                                            }
                                        />
                                    </th>
                                    <th>
                                        <div style={{ display: "flex", gap: 6 }}>
                                            <input
                                                className="pa-filter-input"
                                                placeholder="First"
                                                value={filters.raisedByFirstName}
                                                data-testid="polaris-support-filter-first-name"
                                                onChange={(e) =>
                                                    setFilters((f) => ({
                                                        ...f,
                                                        raisedByFirstName: e.target.value,
                                                    }))
                                                }
                                            />
                                            <input
                                                className="pa-filter-input"
                                                placeholder="Last"
                                                value={filters.raisedByLastName}
                                                data-testid="polaris-support-filter-last-name"
                                                onChange={(e) =>
                                                    setFilters((f) => ({
                                                        ...f,
                                                        raisedByLastName: e.target.value,
                                                    }))
                                                }
                                            />
                                        </div>
                                    </th>
                                    <th>
                                        <SupportTicketSelect
                                            value={filters.severity}
                                            options={SEVERITY_FILTER_OPTIONS}
                                            size="compact"
                                            data-testid="polaris-support-filter-severity"
                                            onChange={(next) =>
                                                setFilters((f) => ({
                                                    ...f,
                                                    severity: next as SupportTicketSeverity | "",
                                                }))
                                            }
                                        />
                                    </th>
                                    <th>
                                        <SupportTicketSelect
                                            value={filters.status}
                                            options={STATUS_FILTER_OPTIONS}
                                            size="compact"
                                            data-testid="polaris-support-filter-status"
                                            onChange={(next) =>
                                                setFilters((f) => ({
                                                    ...f,
                                                    status: next as SupportTicketStatus | "",
                                                }))
                                            }
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            style={{ textAlign: "center", padding: 24, color: "var(--muted)" }}
                                        >
                                            No support tickets found
                                        </td>
                                    </tr>
                                ) : (
                                    tickets.map((ticket) => (
                                        <tr
                                            key={ticket.id}
                                            className="pa-support-row"
                                            data-testid={`polaris-support-row-${ticket.id}`}
                                            onClick={() => void openDetail(ticket)}
                                        >
                                            <td>{ticket.ticketNumber}</td>
                                            <td>{formatSupportTicketDate(ticket.createdAt)}</td>
                                            <td>{ticket.title}</td>
                                            <td>{ticket.companyName || "—"}</td>
                                            <td>
                                                {[ticket.raisedByFirstName, ticket.raisedByLastName]
                                                    .filter(Boolean)
                                                    .join(" ") || "—"}
                                            </td>
                                            <td>
                                                <span
                                                    className={`support-ticket-severity support-ticket-severity--${ticket.severity.toLowerCase()}`}
                                                >
                                                    {formatSupportTicketSeverity(ticket.severity)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="pa-badge">
                                                    {formatSupportTicketStatus(ticket.status)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={page}
                        totalPages={Math.max(totalPages, 1)}
                        totalElements={totalElements}
                        pageSize={pageSize}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        onPageChange={setPage}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </>
            )}

            <SupportTicketDetailModal
                open={detailOpen}
                ticket={detail}
                onClose={() => setDetailOpen(false)}
                onUpdated={handleUpdated}
                polarisAdmin
            />
        </section>
    );
}
